import { formatUnits, parseUnits } from 'viem';
import { USDC_DECIMALS, TOKEN_DECIMALS, LEVERAGE_DECIMALS } from './precision.js';
import type { RawTrade } from './raw.js';

// ─────────────────────────────────────────────────────────────────────────────
// Formatters  (raw on-chain bigint string → human-readable number)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a USDC value (6 decimals) to a JS number.
 * Safe for undefined / null / pre-formatted floats.
 */
export function formatUsdc(value?: bigint | string | number | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.includes('.')) return parseFloat(value);
  return Number(formatUnits(BigInt(value), USDC_DECIMALS));
}

/**
 * Format a token value (18 decimals) to a JS number.
 * Used for prices, OI amounts, and funding / rollover accumulators.
 */
export function formatTokens(value?: bigint | string | number | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.includes('.')) return parseFloat(value);
  return Number(formatUnits(BigInt(value), TOKEN_DECIMALS));
}

/**
 * Format a leverage value (2 decimals) to a JS number.
 * e.g. "1000" → 10 (10×).
 */
export function formatLeverage(value?: bigint | string | number | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.includes('.')) return parseFloat(value);
  return Number(formatUnits(BigInt(value), LEVERAGE_DECIMALS));
}

// ─────────────────────────────────────────────────────────────────────────────
// Parsers  (human-readable number → raw on-chain bigint)
// ─────────────────────────────────────────────────────────────────────────────

/** Parse a USDC amount to bigint with 6 decimals (e.g. "100.50" → 100_500_000n). */
export function parseUsdc(value: number | string): bigint {
  return parseUnits(value.toString(), USDC_DECIMALS);
}

/** Parse a price / token amount to bigint with 18 decimals. */
export function parseTokens(value: number | string): bigint {
  return parseUnits(value.toString(), TOKEN_DECIMALS);
}

/** Parse leverage to bigint with 2 decimals (e.g. 10 → 1000n). */
export function parseLeverage(value: number | string): bigint {
  return parseUnits(value.toString(), LEVERAGE_DECIMALS);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the effective max leverage string for a trade.
 * Prefers pair.maxLeverage → group.maxLeverage → "1".
 */
export function getMaxLeverage(trade: RawTrade): string {
  return trade.pair.maxLeverage && trade.pair.maxLeverage !== '0'
    ? trade.pair.maxLeverage
    : trade.pair.group?.maxLeverage ?? '1';
}

/**
 * Seconds elapsed since the pair's last price / funding snapshot.
 * Used in dynamic spread calculations.
 */
export function timeElapsedSince(pair: { lastUpdateTimestamp?: string; lastFundingTime?: string }): string {
  const now = Math.floor(Date.now() / 1000);
  const last =
    parseInt(pair.lastUpdateTimestamp ?? '0') ||
    parseInt(pair.lastFundingTime ?? '0');
  return (now - last).toString();
}
